import { useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import { useTranslation } from 'react-i18next';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { SlaStatusTag } from '~/app/landing/sla/shared/components/SlaStatusTag';
import { SlaPartnerKindDot } from '~/app/landing/sla/shared/components/SlaPartnerKindDot';
import { SlaTypeDot } from '~/app/landing/sla/shared/components/SlaTypeDot';
import type { OemRateTableRow } from '~/app/landing/sla/shared/utils/oem-rates-table.utils';
import { SLA_RATE_WINDOW_UNIT_COLORS } from '~/app/landing/sla/shared/utils/sla-status.utils';
import { formatCurrency } from '~/app/landing/sla/shared/utils/sla-format.utils';

type PartnerArgs = {
  onEdit: (row: OemPartnerGroupDto) => void;
  canManage: boolean;
};

export function useOemPartnerColumns({ onEdit, canManage }: PartnerArgs) {
  const { t } = useTranslation('sla');

  return useMemo(
    () => [
      {
        title: t('oems.columns.partner'),
        dataIndex: 'partnerName',
        key: 'partnerName',
        render: (name: string) => (
          <Flex align="center" gap={10}>
            <SlaPartnerKindDot variant="oem" />
            <Typography.Text strong>{name}</Typography.Text>
          </Flex>
        ),
      },
      {
        title: t('oems.columns.rates'),
        key: 'rates',
        width: 120,
        render: (_: unknown, row: OemPartnerGroupDto) =>
          row.slaContract?.rateEntries.length ?? 0,
      },
      {
        title: t('oems.columns.status'),
        key: 'status',
        width: 120,
        render: (_: unknown, row: OemPartnerGroupDto) =>
          row.slaContract ? (
            <SlaStatusTag status={row.slaContract.status} />
          ) : (
            '—'
          ),
      },
      ...(canManage
        ? [
            {
              key: 'actions',
              width: 56,
              align: 'center' as const,
              render: (_: unknown, row: OemPartnerGroupDto) => (
                <TableRowActionsMenu
                  items={[
                    {
                      key: 'edit',
                      label: t('actions.edit'),
                      icon: <EditOutlined />,
                      onClick: () => onEdit(row),
                    },
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [canManage, onEdit, t],
  );
}

export function useOemRatesColumns() {
  const { t } = useTranslation('sla');

  return useMemo(
    () => [
      {
        title: t('oems.columns.partner'),
        dataIndex: 'partnerName',
        key: 'partnerName',
        width: 200,
        onCell: (row: OemRateTableRow) => ({
          rowSpan: row.partnerRowSpan,
        }),
        render: (name: string) => (
          <Flex align="center" gap={10}>
            <SlaPartnerKindDot variant="oem" />
            <Typography.Text strong>{name}</Typography.Text>
          </Flex>
        ),
      },
      {
        title: t('oems.columns.window'),
        key: 'window',
        width: 140,
        render: (_: unknown, row: OemRateTableRow) =>
          row.windowValue != null && row.windowUnit ? (
            <Flex align="center" gap={8}>
              <SlaTypeDot
                color={
                  SLA_RATE_WINDOW_UNIT_COLORS[
                    row.windowUnit as keyof typeof SLA_RATE_WINDOW_UNIT_COLORS
                  ] ?? '#94a3b8'
                }
                size={8}
              />
              <span>
                {row.windowValue} {row.windowUnit}
              </span>
            </Flex>
          ) : (
            '—'
          ),
      },
      {
        title: t('oems.columns.cost'),
        key: 'cost',
        width: 120,
        render: (_: unknown, row: OemRateTableRow) =>
          row.costAmount != null
            ? formatCurrency(row.costAmount, row.currency ?? 'EUR')
            : '—',
      },
    ],
    [t],
  );
}
